'use strict';

/**
 * Tree Filter
 *
 * Filters a tree like data structure of arbitrary depth by checking the tree nodes for the
 * presence of search string/regex found in a value assigned to a specific property(s).
 *
 * Key functionality:
/* - Does not alter the original data
 * - Filtered tree preserves the general structure of the original data
 * - If parent-node contains the search it will NOT attempt to filter child nodes
 * - If parent-node property DOES NOT contain the search string, it will search child-nodes.
 * - If a child-node matches than all its ancestors up to the root of the tree match as well
 * - If a child-node contains the search string BUT parent-node does not prune all child-nodes
 *   that do not also contain the search
 *
 * Assumed Data Structure:

    [
      {
        title: 'value',
        key: 'search term'
        childNodes: [ // key is configurable
          {id: 1},
          {id: 2},
          {id: 3},
        ]
      },
      {
        title: 'value',
        key: 'search term'
      },
      {
        title: 'value',
      }




      {
        title: 'value',
        meta1: 'some other data'
        childNodes: [
          {
            id: 1,
            key: 'search term'
          },
          {id: 2},
          {
            id: 3,
            childNodes: [
              {
                id: 1,
                key: 'search term'
              }
            ]
          },
        ]
      }
    ]

 *
 * Returned Data Structure... checking for matches on key "key" and value "search term"

  [
    {
      title: 'value',
      key: 'search term'
      childNodes: [
        {id: 1}
        {id: 2}
        {id: 3}
      ]
    },
    {
      title: 'value',
      meta1: 'some other data'
      childNodes: [
        {
          id: 1
          key: 'search term'
        },
        {
          id: 3
          childNodes: [
            {
              id: 1,
              key: 'search term'
            }
          ]
        },
      ]
    }
  ]

 */

var _ = require('lodash');

module.exports = treeFilter;

function treeFilter() {

  var keysArr = [];
  var maxDepth = 6; // gets overwritten if maxDepth {[number]} is passed as a directive attrbiute)

  /**
   * Filter signature
   *
   * @param  {Object|Array} list
   * @param  {String|Regex} pattern
   * @param  {String} keys Comma delimited list of key names to search
   * @param  {Integer} depth How far down the tree to traverse before stopping
   *
   * @return {Object|Array} filtered list
   */
  return function (list, pattern, keys, depth) {
    if (typeof list !== 'object' || !pattern || !keys) {
      return list;
    }
    maxDepth = depth || maxDepth;
    keysArr = keys.split(/,\s?/gi);

    var filtered = traverse(list, pattern, 0);

    // traverse will return undefined if no items are found but the UI will expect a list like
    // object so we return an empty list of the same type as the source list.
    if (typeof filtered === 'undefined') {
      return Array.isArray(list) ? [] : {};
    } else {
      return filtered;
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // Helpers
  /////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Traverse the tree, checking each node for the search pattern
   *
   * @param  {object|array} node
   * @param  {string} pattern string or regex pattern to search for
   * @param  {number} currentDepth integer representing how far down the tree we have descended
   * @return {object|array} filtered tree-like data
   */
  function traverse(node, pattern, currentDepth) {

    // stop if we are checking to deep
    if (currentDepth > maxDepth) {
      return;
    }

    // if this node contains the search pattern... return the whole node
    if (nodeContainsSearch(node, pattern)) {
      return node;
    }
    // continue traversing the tree
    else {
        var filtered; // filtered copy of the current node

        _.forEach(node, function (childNode, key) {
          if (!shouldSkip(childNode, key)) {
            var _filtered = traverse(childNode, pattern, currentDepth + 1);

            if (_filtered) {

              // if we haven't already created the filtered object/array and set it...
              // determine the type of the current node and set filtered to the correct type
              if (!filtered) {
                if (Array.isArray(node)) {
                  filtered = [];
                } else {
                  filtered = {};
                }
              }

              // save the filtered data
              if (Array.isArray(filtered)) {
                filtered.push(_filtered);
              } else {
                filtered[key] = _filtered;
              }
            }
          }
        });

        // if the current node is an object and one of it's children contain the search
        // then we also need to copy over the leaf nodes OR the special meta property
        // for this node because the leaf nodes and/or meta property are assumed to
        // contain relevant metadata which will be needed when rendering
        if (filtered) {
          if (node && typeof node === 'object' && !Array.isArray(node)) {
            _.extend(filtered, copyLeaves(node));
          }
        }

        return filtered;
      }
  }

  /**
   * Retrieves only the leaf properties of the node.
   * Leaves are any values that are not objects or arrays.
   *
   * @param  {object} node the node to copy leaves from
   * @return {object} object containing all the leaves
   */
  function copyLeaves(node) {
    var leaves = {};

    _.forEach(node, function (val, key) {
      if (isLeaf(val, key)) {
        leaves[key] = val;
      }
    });

    return leaves;
  }

  /**
   * Checks the current node to see if it contains the search value.
   * Will not check nested objects/arrays.
   *
   * @param  {Object} node the current object to check.
   * @param  {String} pattern the regex to match
   * @return {Boolean} boolean indicating whether or not the node contains the search
   */
  function nodeContainsSearch(node, pattern) {

    var found = false;
    pattern = pattern.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
    try {
      pattern = new RegExp(pattern, 'gi');
    } catch (e) {
      // convert lone "\" to its equivalent html character
      pattern = pattern.replace(/\\/g, '&#92;');
      pattern = new RegExp(pattern, 'gi');
    }

    _.forEach(node, function (val, key) {

      if (found || val && typeof val === 'object') {
        return;
      }

      if (!! ~keysArr.indexOf(key) && pattern.test(val)) {
        found = true;
      }
    });

    return found;
  }

  function shouldSkip(node, key) {
    var isPrivate = key.toString().charAt(0) === '$';
    return isPrivate || !node || typeof node !== 'object';
  }

  function isLeaf(node, key) {
    var metaKey = '_meta'; // TODO make this an option
    var isPrivate = key.toString().charAt(0) === '$';
    return key === metaKey || node && typeof node !== 'object' && !isPrivate;
  }
}


// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/filter/es5/filters/treeFilter.js